"use client";

import { LoadingModal } from "@/components/core/LoadingModal";
import { notifyError } from "@/utils/monitoring";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Action, UserJourney, useLoginAnalytics } from "./AnalyticsProvider";
import { usePassportProvider } from "./PassportProvider";

interface LoginModalContextProps {
  openLoginModal: (callback?: () => Promise<void>) => Promise<void>;
  isModalOpen: boolean;
}

const LoginModalContext = createContext<LoginModalContextProps | undefined>(
  undefined,
);

export const LoginModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const { track: trackLogin } = useLoginAnalytics();
  const { login, isLoggingIn, isLoggedIn, cancelLogin } = usePassportProvider();

  const openLoginModal = useCallback(
    async (callback?: () => Promise<void>) => {
      if (isModalOpen || isLoggingIn || isLoggedIn) return;

      try {
        // Call login to start appending Passport's embedded login prompt iFrame
        // before rendering the LoginModal
        const loginPromise = login();
        setIsModalOpen(true);

        // Track Login_Viewed when modal opens
        trackLogin({
          action: Action.VIEWED,
          extras: {
            screen: "Login",
            userJourney: UserJourney.ONBOARDING,
          },
        });

        await loginPromise;
        setIsModalOpen(false);

        if (callback) {
          await callback();
        }
      } catch (error) {
        notifyError(error, "login");
        setIsModalOpen(false);
      }
    },
    [isModalOpen, isLoggingIn, isLoggedIn, login, trackLogin],
  );

  const closeLoginModal = useCallback(() => {
    cancelLogin();
    setIsModalOpen(false);
  }, [cancelLogin]);

  return (
    <LoginModalContext.Provider value={{ openLoginModal, isModalOpen }}>
      {children}
      <LoadingModal
        loading={isLoggingIn}
        text={[t("logging_in"), t("logging_in_popup")]}
        closeable={true}
        cancelLogin={closeLoginModal}
        loopingText={false}
      />
    </LoginModalContext.Provider>
  );
};

export const useLoginModal = () => {
  const context = useContext(LoginModalContext);
  if (!context) {
    throw new Error("useLoginModal must be used within a LoginModalProvider");
  }
  return context;
};
